<template>
  <el-dialog
    class="dialog-wrapper"
    visible
    :show-close="false"
    @close="closeUpdate"
  >
    <ContentHeader
      slot="title"
      :title="$t('customerSampleAnalyses.deafultLocationSettings')"
    />
    <ValidationObserver
      ref="userForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="16">
            <FormItem
              rules="required"
              :label="$t('customerSampleAnalyses.defaultLocation.modality')"
            >
              <el-switch
                v-model="locationAssignmentStrategy"
                :active-text="
                  $t('customerSampleAnalyses.defaultLocation.automatic')
                "
                :inactive-text="
                  $t('customerSampleAnalyses.defaultLocation.manual')
                "
                active-value="automatic"
                inactive-value="manual"
                @change="updateLocationAssignmentStrategy"
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="6">
            <FormItem :label="$t('users.create.laboratory')">
              <el-select
                v-model="formData.laboratory"
                placeholder=""
              >
                <el-option
                  v-for="laboratory in selectableLaboratories"
                  :key="laboratory"
                  :value="laboratory"
                  :label="$t(`constants.${laboratory}`)"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('users.create.location')">
              <el-select
                v-model="formData.locationId"
                :disabled="!formData.laboratory || formData.laboratory === ''"
                placeholder=""
              >
                <el-option
                  v-for="location in locations"
                  :key="location.value"
                  :value="location.value"
                  :label="location.label"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="4">
            <el-form-item>
              <span slot="label">{{ $t('global.add') }}</span>
              <el-button
                class="width-100"
                type="primary"
                :disabled="
                  !formData.laboratory ||
                  formData.laboratory === '' ||
                  !formData.locationId ||
                  formData.locationId === ''
                "
                @click.prevent="addDefaultLocation"
              >
                {{ $t('global.add') }}
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <div class="table-wrapper">
              <el-table :data="departmentDefaultLocations">
                <el-table-column
                  prop="department"
                  :label="$t('users.create.laboratory')"
                  :formatter="
                    (row, column, cellValue) => $t(`constants.${cellValue}`)
                  "
                />
                <el-table-column
                  prop="location.name"
                  :label="$t('users.create.location')"
                >
                  <template slot-scope="scope">
                    <span v-if="editRowId !== scope.row.id">{{
                      scope.row.location.name
                    }}</span>
                    <span v-if="editRowId === scope.row.id">
                      <el-select
                        v-model="scope.row.locationId"
                        placeholder=""
                      >
                        <el-option
                          v-for="location in locations"
                          :key="location.value"
                          :value="location.value"
                          :label="location.label"
                        />
                      </el-select>
                    </span>
                  </template>
                </el-table-column>
                <el-table-column
                  :label="$t('global.actions')"
                  width="100"
                >
                  <template slot-scope="scope">
                    <el-button
                      v-if="editRowId === scope.row.id"
                      class="table-action-button"
                      icon="fas fa-save"
                      @click="editDefaultLocation(scope.row)"
                    />
                    <el-button
                      v-if="editRowId === scope.row.id"
                      class="table-action-button"
                      icon="fas fa-times"
                      @click="removeDefaultLocation()"
                    />
                    <el-button
                      v-if="editRowId !== scope.row.id"
                      class="table-action-button"
                      icon="fas fa-edit"
                      @click="activeDefaultLocation(scope.row)"
                    />
                    <el-button
                      v-if="editRowId !== scope.row.id"
                      class="table-action-button"
                      icon="fas fa-times"
                      @click="deleteDefaultLocation(scope.row)"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid || departmentDefaultLocations.length === 0"
            @click.prevent="handleSubmit(closeUpdate)"
          >
            {{ $t('customerSampleAnalyses.defaultLocation.closeUpdate') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import {
    departmentDefaultLocationsApi,
    locationsManagementApi,
    configApi,
  } from '@/api';
  import { laboratories } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';

  export default {
    name: 'DefaultLocationSettingsModal',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
    },
    data() {
      return {
        locationAssignmentStrategy: 'manual',
        formData: {
          laboratory: '',
          locationId: '',
        },
        departmentDefaultLocations: [],
        laboratories,
        locations: [],
        editRowId: null,
      };
    },
    computed: {
      selectableLaboratories() {
        const selectableLaboratories = { ...this.laboratories };
        this.departmentDefaultLocations.map((ddl) => {
          if (selectableLaboratories[ddl.department]) {
            delete selectableLaboratories[ddl.department];
          }
          return true;
        });
        return selectableLaboratories;
      },
    },
    async created() {
      await this.tableRows();
    },
    methods: {
      async tableRows() {
        try {
          const locationAssignmentStrategy =
            await configApi.getLocationAssignmentStrategy();
          this.locationAssignmentStrategy =
            locationAssignmentStrategy.data['locations.assignmentStrategy'];
          const departmentDefaultLocationsIndex =
            await departmentDefaultLocationsApi.index();
          this.departmentDefaultLocations =
            departmentDefaultLocationsIndex.data;
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            ''
          );
          this.locations = response.data.map((c) => ({
            label: c.name,
            value: c.id,
          }));
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      closeUpdate() {
        this.$emit('dialogClose', { success: true });
      },
      async activeDefaultLocation(row) {
        this.editRowId = row.id;
      },
      async editDefaultLocation(row) {
        try {
          this.editRowId = null;
          const data = {
            department: row.department,
            locationId: row.locationId,
          };
          await departmentDefaultLocationsApi.patch(row.id, data);
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async removeDefaultLocation() {
        this.editRowId = null;
      },
      async addDefaultLocation() {
        try {
          const data = {
            department: this.formData.laboratory,
            locationId: this.formData.locationId,
          };
          await departmentDefaultLocationsApi.create(data);
          this.formData = {
            laboratory: '',
            locationId: '',
          };
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async deleteDefaultLocation(row) {
        try {
          await departmentDefaultLocationsApi.delete(row.id);
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async updateLocationAssignmentStrategy() {
        try {
          await configApi.patchLocationAssignmentStrategy(
            this.locationAssignmentStrategy
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
    },
  };
</script>
